import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import { getEnv } from 'mobx-state-tree';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AutoGraphRounded from '@mui/icons-material/AutoGraphRounded';
import ProceduresIcon from '@assets/neo-procedures-icon.svg';

import { RoutePaths } from '@core/routes/RoutePaths';
import { IStoresEnv } from '@core/storesEnv';
import { useExecutionsGridUIStore, useUserPermissionsUIStore } from '@core/useStores';
import { Button } from '@shared/components/Button';
import { ThemeProps } from '@styles/theme';
import { getExecutionLifecycleState, getStepCompletionStatus } from 'NeoAnalytics/executionSteps';
import { performGetActorsSummary, performQueryActors } from 'NeoAnalytics/services/getActor';
import { performGetEntity } from 'NeoAnalytics/services/getEntities';
import { performGetExecutions, performGetExecutionsFullData } from 'NeoAnalytics/services/getExecutions';
import { performGetGradings } from 'NeoAnalytics/services/getGradings';
import { performGetProcedures } from 'Procedures/services';

import { useStyles } from './ProcedureSummaryPage.styles';

// --------------- Types -------------------------------------------------------

type RangeKey = '7d' | '30d' | '90d' | 'all';

type SummaryExecution = {
    id: string;
    entityId: string;
    entityName: string;
    entityVersion: string | null;
    executionMode: string;
    platform: string;
    sessionType: string;
    startTime: string | null;
    endTime: string | null;
    durationSeconds: number | null;
    actorIds: string[];
    endReason: string | null;
    isCompleted: boolean | null;
    success: boolean | null;
};

type SummaryActor = {
    id: string;
    displayName: string;
    email: string | null;
    roles: string[];
};

type SummaryGrading = {
    executionId: string;
    isReviewed: boolean;
    reviewCount: number;
};

type SummaryActorSummary = {
    actorId: string;
    displayName: string;
    totalExecutions: number;
};

type ProcedureDetail = {
    id: string;
    name: string | null;
    version: string | null;
    description: string | null;
};

type IssueMetric = {
    name: string;
    entityId?: string | null;
    executionId?: string | null;
    count: number;
    averageDuration?: number | null;
    failureRate?: number | null;
};

type SummaryIssues = {
    longestSteps: IssueMetric[];
    failedSteps: IssueMetric[];
    abortedExecutions: IssueMetric[];
    abandonedExecutions: IssueMetric[];
    abandonedSteps: IssueMetric[];
};

type SummaryPayload = {
    executions: SummaryExecution[];
    actors: SummaryActor[];
    gradings: SummaryGrading[];
    actorSummaries: SummaryActorSummary[];
    procedureDetail: ProcedureDetail | null;
    issues: SummaryIssues;
    loadedAt: Date;
};

type SummaryState = {
    isLoading: boolean;
    error: string | null;
    payload: SummaryPayload | null;
    issuesLoading: boolean;
};

type ActivityPoint = {
    key: string;
    label: string;
    value: number;
    completed: number;
    failed: number;
    aborted: number;
    abandoned: number;
    ongoing: number;
    executionIds: string[];
};

type ExecutionActorContext = {
    displayName: string | null;
    roleName: string | null;
    email: string | null;
};

type MixStatStatusBreakdown = {
    completed: number;
    failed: number;
    aborted: number;
    abandoned: number;
    ongoing: number;
};

type MixStatActorDetail = { id: string; displayName: string; count: number };
type MixStatTimelinePoint = { date: string; count: number };

type MixStat = {
    label: string;
    count: number;
    pct: number;
    avgDuration: number | null;
    minDuration: number | null;
    maxDuration: number | null;
    durations: number[];
    completionRate: number;
    uniqueActors: number;
    actorDetails: MixStatActorDetail[];
    timeline: MixStatTimelinePoint[];
    statusBreakdown: MixStatStatusBreakdown;
};

type ChipModalState = {
    stat: MixStat;
    chipType: 'sessions' | 'actors' | 'avg';
    title: string;
    anchor: { top: number; left: number; width: number; height: number };
} | null;


const EMPTY_ISSUES: SummaryIssues = {
    longestSteps: [],
    failedSteps: [],
    abortedExecutions: [],
    abandonedExecutions: [],
    abandonedSteps: [],
};

const RANGE_OPTIONS: Array<{ key: RangeKey; label: string }> = [
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: 'all', label: 'All time' },
];

// --------------- Helpers -----------------------------------------------------

function getString(value: unknown): string | null {
    if (typeof value === 'string') {
        const trimmed = value.trim();

        return trimmed.length ? trimmed : null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    return null;
}

function getNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {return value;}
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

function getBoolean(value: unknown): boolean | null {
    if (typeof value === 'boolean') {return value;}
    if (typeof value === 'string') {
        const n = value.trim().toLowerCase();
        if (['true', '1', 'yes'].includes(n)) {return true;}
        if (['false', '0', 'no'].includes(n)) {return false;}
    }

    return null;
}

function asArray<T = Record<string, any>>(value: unknown): T[] {
    if (Array.isArray(value)) {return value as T[];}
    if (Array.isArray((value as any)?.entities)) {return (value as any).entities as T[];}
    if (Array.isArray((value as any)?.data)) {return (value as any).data as T[];}

    return [];
}

function parseDurationToSeconds(
    duration: string | null,
    startTime?: string | null,
    endTime?: string | null,
): number | null {
    if (duration) {
        const match = duration.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
        if (match) {
            const h = Number(match[1]);
            const m = Number(match[2]);
            const s = Number(match[3]);
            const f = match[4] ? Number(`0.${match[4]}`) : 0;

            return h * 3600 + m * 60 + s + f;
        }
    }
    const start = Date.parse(startTime ?? '');
    const end = Date.parse(endTime ?? '');
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
        return (end - start) / 1000;
    }

    return null;
}

function formatCompactNumber(value: number): string {
    return new Intl.NumberFormat('en', {
        notation: 'compact',
        maximumFractionDigits: value >= 1000 ? 1 : 0,
    }).format(value);
}

function formatWholePercent(value: number): string {
    return `${Math.round(value)}%`;
}

function formatDuration(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {return 'n/a';}
    if (value < 60) {return `${Math.round(value)} sec`;}
    if (value < 3600) {return `${Math.round(value / 60)} min`;}

    return `${(value / 3600).toFixed(1)} h`;
}

function formatDateLabel(value: string | null): string {
    const parsed = Date.parse(value ?? '');
    if (!Number.isFinite(parsed)) {return 'Unknown time';}

    return new Intl.DateTimeFormat('en', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(parsed);
}

function formatSubtleDateLabel(value: string): string {
    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) {return 'just now';}

    return new Intl.DateTimeFormat('en', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(parsed);
}

function startOfDay(date: Date) {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);

    return result;
}

function getRangeStart(range: RangeKey): Date | null {
    if (range === 'all') {return null;}
    const now = new Date();
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const from = new Date(now.getTime() - (days - 1) * 24 * 60 * 60 * 1000);

    return startOfDay(from);
}

function getRangeDays(range: RangeKey): number {
    if (range === '7d') {return 7;}
    if (range === '30d') {return 30;}
    if (range === '90d') {return 90;}

    return 30;
}

function formatLocalDateTime(value: Date): string {
    const pad = (part: number) => String(part).padStart(2, '0');

    return [
        `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`,
        `${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`,
    ].join('T');
}

function normalizeExecution(raw: Record<string, any>): SummaryExecution {
    return {
        id: getString(raw.id) ?? '',
        entityId: getString(raw.entityId) ?? '',
        entityName: getString(raw.entityName) ?? 'Unknown procedure',
        entityVersion: getString(raw.entityVersion),
        executionMode: getString(raw.executionMode) ?? 'Unknown',
        platform: getString(raw.platform) ?? 'Unknown',
        startTime: getString(raw.startTime),
        endTime: getString(raw.endTime),
        durationSeconds: parseDurationToSeconds(
            getString(raw.duration),
            getString(raw.startTime),
            getString(raw.endTime),
        ),
        actorIds: Array.isArray(raw.actorIds)
            ? raw.actorIds
                .map((id: unknown) => getString(id))
                .filter((id): id is string => Boolean(id))
            : [],
        sessionType: (() => {
            const n = Array.isArray(raw.actorIds) ? raw.actorIds.length : 0;
            if (n <= 1) {return 'Solo';}
            if (n === 2) {return 'Paired';}

            return 'Group';
        })(),
        endReason: getString(raw.endReason),
        isCompleted: getBoolean(raw.isCompleted),
        success: getBoolean(raw.success),
    };
}

function normalizeActor(raw: Record<string, any>): SummaryActor {
    const displayName =
        getString(raw.displayName) ??
        ([getString(raw.firstName), getString(raw.lastName)].filter(Boolean).join(' ') ||
        getString(raw.email)) ??
        'Unknown actor';

    return {
        id: getString(raw.id) ?? getString(raw.actorId) ?? '',
        displayName,
        email: getString(raw.email),
        roles: Array.isArray(raw.roles)
            ? raw.roles
                .map((role: unknown) =>
                    typeof role === 'string' ? role : getString((role as any)?.name),
                )
                .filter((role): role is string => Boolean(role))
            : [],
    };
}

function normalizeGrading(raw: Record<string, any>): SummaryGrading {
    const reviewCount = Array.isArray(raw.reviews)
        ? raw.reviews.length
        : (getNumber(raw.reviewCount) ?? 0);

    return {
        executionId: getString(raw.executionId) ?? '',
        isReviewed: Boolean(getBoolean(raw.isReviewed)) || reviewCount > 0,
        reviewCount,
    };
}

function normalizeActorSummary(raw: Record<string, any>): SummaryActorSummary {
    return {
        actorId: getString(raw.actorId) ?? getString(raw.id) ?? '',
        displayName:
            getString(raw.displayName) ??
            ([getString(raw.firstName), getString(raw.lastName)].filter(Boolean).join(' ') ||
            'Unknown actor'),
        totalExecutions:
            getNumber(raw.totalExecutions) ??
            getNumber(raw.executionsCount) ??
            getNumber(raw.executionCount) ??
            getNumber(raw.totalSessions) ??
            0,
    };
}

function getStatusTone(status: string, theme: ThemeProps) {
    switch (status) {
                    case 'Completed':
                        return { color: theme.colors.green, background: 'rgba(16, 185, 129, 0.14)' };
                    case 'Failed':
                    case 'Exception':
                        return { color: theme.colors.red, background: 'rgba(220, 38, 38, 0.12)' };
                    case 'Stopped':
                        return { color: theme.isDark ? '#e8a060' : '#7a5b00', background: 'rgba(245, 212, 81, 0.22)' };
                    case 'Abandoned':
                        return { color: theme.colors.grey_text, background: 'rgba(102, 119, 128, 0.14)' };
                    default:
                        return { color: '#d18100', background: 'rgba(234, 179, 8, 0.18)' };
    }
}

function getExecutionStatusLabel(execution: SummaryExecution): string {
    const lifecycle = getExecutionLifecycleState(execution);
    const normalizedEndReason = String(execution.endReason ?? '').toLowerCase();
    if (lifecycle === 'failed') {
        return normalizedEndReason.includes('exception') || execution.isCompleted === false
            ? 'Exception'
            : 'Failed';
    }
    if (lifecycle === 'aborted') {return 'Stopped';}
    if (lifecycle === 'abandoned') {return 'Abandoned';}
    if (lifecycle === 'ongoing') {return 'In Progress';}

    return 'Completed';
}

function buildIssues(fullDataExecutions: Array<Record<string, any>>): SummaryIssues {
    const stepMap = new Map<string, {
        count: number;
        durationTotal: number;
        failures: number;
        latestExecutionId: string | null;
        latestEntityId: string | null;
        latestTimestamp: number;
        latestFailedExecutionId: string | null;
        latestFailedEntityId: string | null;
        latestFailedTimestamp: number;
    }>();
    const abortedCounts = new Map<string, { executionId: string | null; count: number }>();
    const abandonedCounts = new Map<string, { executionId: string | null; count: number }>();
    const abandonedStepCounts = new Map<string, { count: number; executionId: string | null }>();

    fullDataExecutions.forEach(execution => {
        const executionRecordId = getString(execution.id);
        const executionTimestamp = Date.parse(getString(execution.startTime) ?? '');
        const lifecycle = getExecutionLifecycleState({
            endTime: getString(execution.endTime),
            endReason: getString(execution.endReason),
            isCompleted: getBoolean(execution.isCompleted),
            success: getBoolean(execution.success),
            startTime: getString(execution.startTime),
        });

        const actorLabel =
            getString(execution.actorName) ??
            getString(execution.actorDisplayName) ??
            (Array.isArray(execution.actorIds) && execution.actorIds.length
                ? `Actor ${String(execution.actorIds[0]).slice(-4)}`
                : null) ??
            'Unknown actor';

        if (lifecycle === 'aborted') {
            const existing = abortedCounts.get(actorLabel) ?? { executionId: executionRecordId, count: 0 };
            existing.count += 1;
            abortedCounts.set(actorLabel, existing);
        }

        if (lifecycle === 'abandoned') {
            const existing = abandonedCounts.get(actorLabel) ?? { executionId: executionRecordId, count: 0 };
            existing.count += 1;
            abandonedCounts.set(actorLabel, existing);

            const steps = Array.isArray(execution.steps) ? execution.steps : [];
            if (steps.length > 0) {
                const lastStep = steps[steps.length - 1];
                const stepName = getString(lastStep?.name) ?? 'Unnamed step';
                const existingStep = abandonedStepCounts.get(stepName) ?? {
                    count: 0,
                    executionId: executionRecordId,
                };
                existingStep.count += 1;
                abandonedStepCounts.set(stepName, existingStep);
            }
        }

        const steps = Array.isArray(execution.steps) ? execution.steps : [];
        steps.forEach(step => {
            const name = getString(step?.name) ?? 'Unnamed step';
            const duration = parseDurationToSeconds(
                getString(step?.duration),
                getString(step?.startTime),
                getString(step?.endTime),
            );
            const status = getStepCompletionStatus(step);
            const aggregate = stepMap.get(name) ?? {
                count: 0,
                durationTotal: 0,
                failures: 0,
                latestExecutionId: null,
                latestEntityId: null,
                latestTimestamp: Number.NEGATIVE_INFINITY,
                latestFailedExecutionId: null,
                latestFailedEntityId: null,
                latestFailedTimestamp: Number.NEGATIVE_INFINITY,
            };

            aggregate.count += 1;
            if (duration !== null) {aggregate.durationTotal += duration;}
            if (Number.isFinite(executionTimestamp) && executionTimestamp >= aggregate.latestTimestamp) {
                aggregate.latestTimestamp = executionTimestamp;
                aggregate.latestExecutionId = executionRecordId;
            }
            if (status === 'failed' || status === 'aborted') {
                aggregate.failures += 1;
                if (
                    Number.isFinite(executionTimestamp) &&
                    executionTimestamp >= aggregate.latestFailedTimestamp
                ) {
                    aggregate.latestFailedTimestamp = executionTimestamp;
                    aggregate.latestFailedExecutionId = executionRecordId;
                }
            }
            stepMap.set(name, aggregate);
        });
    });

    const longestSteps = Array.from(stepMap.entries())
        .map(([name, agg]) => ({
            name,
            count: agg.count,
            averageDuration: agg.count ? agg.durationTotal / agg.count : null,
            executionId: agg.latestExecutionId,
        }))
        .filter(item => item.averageDuration !== null)
        .sort((a, b) => (b.averageDuration ?? 0) - (a.averageDuration ?? 0))
        .slice(0, 5);

    const failedSteps = Array.from(stepMap.entries())
        .map(([name, agg]) => ({
            name,
            count: agg.failures,
            failureRate: agg.count ? agg.failures / agg.count : 0,
            executionId: agg.latestFailedExecutionId ?? agg.latestExecutionId,
        }))
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count || (b.failureRate ?? 0) - (a.failureRate ?? 0))
        .slice(0, 5);

    const abortedExecutions = Array.from(abortedCounts.entries())
        .map(([name, data]) => ({ name, executionId: data.executionId, count: data.count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

    const abandonedExecutions = Array.from(abandonedCounts.entries())
        .map(([name, data]) => ({ name, executionId: data.executionId, count: data.count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

    const abandonedSteps = Array.from(abandonedStepCounts.entries())
        .map(([name, data]) => ({ name, count: data.count, executionId: data.executionId }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

    return { longestSteps, failedSteps, abortedExecutions, abandonedExecutions, abandonedSteps };
}

function buildModel(payload: SummaryPayload | null) {
    if (!payload) {return null;}

    const { executions, actors, gradings, actorSummaries, procedureDetail, loadedAt, issues } = payload;

    const actorMap = new Map(actors.map(a => [a.id, a]));
    const uniqueActorIds = new Set(executions.flatMap(e => e.actorIds));
    const gradingByExecutionId = new Map(gradings.map(g => [g.executionId, g]));

    const statusCounters = { completed: 0, failed: 0, aborted: 0, abandoned: 0, ongoing: 0 };
    executions.forEach(e => {
        const lifecycle = getExecutionLifecycleState(e);
        if (lifecycle === 'completed') {statusCounters.completed += 1;}
        if (lifecycle === 'failed') {statusCounters.failed += 1;}
        if (lifecycle === 'aborted') {statusCounters.aborted += 1;}
        if (lifecycle === 'abandoned') {statusCounters.abandoned += 1;}
        if (lifecycle === 'ongoing') {statusCounters.ongoing += 1;}
    });

    const durations = executions
        .map(e => e.durationSeconds)
        .filter((d): d is number => d !== null);
    const avgDuration = durations.length
        ? durations.reduce((sum, v) => sum + v, 0) / durations.length
        : null;
    const gradedCount = gradings.length;
    const reviewedCount = gradings.filter(g => g.isReviewed || g.reviewCount > 0).length;
    const completionRate = executions.length
        ? (statusCounters.completed / executions.length) * 100
        : 0;
    const gradingCoverage = executions.length
        ? (gradedCount / executions.length) * 100
        : 0;

    const actorCounts = new Map<string, number>();
    executions.forEach(e => {
        e.actorIds.forEach(id => actorCounts.set(id, (actorCounts.get(id) ?? 0) + 1));
    });

    const topActors = (
        actorSummaries.length
            ? actorSummaries
            : Array.from(actorCounts.entries()).map(([actorId, totalExecutions]) => ({
                actorId,
                displayName: actorMap.get(actorId)?.displayName ?? 'Unknown actor',
                totalExecutions,
            }))
    )
        .map(item => ({
            ...item,
            displayName: item.displayName || actorMap.get(item.actorId)?.displayName || 'Unknown actor',
            email: actorMap.get(item.actorId)?.email ?? null,
            roles: actorMap.get(item.actorId)?.roles ?? [],
        }))
        .sort((a, b) => b.totalExecutions - a.totalExecutions)
        .slice(0, 8);

    const computeMixStats = (
        groupKey: keyof Pick<SummaryExecution, 'executionMode' | 'platform' | 'sessionType'>,
    ): MixStat[] => {
        const groups = new Map<
            string,
            {
                count: number;
                durationSum: number;
                durationCount: number;
                durations: number[];
                completed: number;
                failed: number;
                aborted: number;
                abandoned: number;
                ongoing: number;
                actorIds: Set<string>;
                dayBuckets: Map<string, number>;
            }
        >();
        executions.forEach(e => {
            const key = String(e[groupKey] || 'Unknown');
            const existing = groups.get(key) ?? {
                count: 0,
                durationSum: 0,
                durationCount: 0,
                durations: [],
                completed: 0,
                failed: 0,
                aborted: 0,
                abandoned: 0,
                ongoing: 0,
                actorIds: new Set<string>(),
                dayBuckets: new Map<string, number>(),
            };
            existing.count += 1;
            if (e.durationSeconds !== null) {
                existing.durationSum += e.durationSeconds;
                existing.durationCount += 1;
                existing.durations.push(e.durationSeconds);
            }
            const lifecycle = getExecutionLifecycleState(e);
            existing[lifecycle] += 1;
            e.actorIds.forEach(id => existing.actorIds.add(id));
            if (e.startTime) {
                const dayKey = e.startTime.slice(0, 10);
                existing.dayBuckets.set(dayKey, (existing.dayBuckets.get(dayKey) ?? 0) + 1);
            }
            groups.set(key, existing);
        });
        const total = executions.length;

        return Array.from(groups.entries())
            .map(([label, data]) => {
                const sortedDurations = data.durations.slice().sort((a, b) => a - b);
                const actorDetails: MixStatActorDetail[] = Array.from(data.actorIds)
                    .map(actorId => ({
                        id: actorId,
                        displayName: actorMap.get(actorId)?.displayName ?? 'Unknown actor',
                        count: executions.filter(
                            e => e.actorIds.includes(actorId) && String(e[groupKey] || 'Unknown') === label
                        ).length,
                    }))
                    .sort((a, b) => b.count - a.count);
                const timeline: MixStatTimelinePoint[] = Array.from(data.dayBuckets.entries())
                    .map(([date, count]) => ({ date, count }))
                    .sort((a, b) => a.date.localeCompare(b.date));

                return {
                    label,
                    count: data.count,
                    pct: total ? (data.count / total) * 100 : 0,
                    avgDuration: data.durationCount ? data.durationSum / data.durationCount : null,
                    minDuration: sortedDurations.length ? sortedDurations[0] : null,
                    maxDuration: sortedDurations.length ? sortedDurations[sortedDurations.length - 1] : null,
                    durations: sortedDurations,
                    completionRate: data.count ? (data.completed / data.count) * 100 : 0,
                    uniqueActors: data.actorIds.size,
                    actorDetails,
                    timeline,
                    statusBreakdown: {
                        completed: data.completed,
                        failed: data.failed,
                        aborted: data.aborted,
                        abandoned: data.abandoned,
                        ongoing: data.ongoing,
                    },
                };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
    };

    const modeStats = computeMixStats('executionMode');
    const platformStats = computeMixStats('platform');
    const sessionTypeStats = computeMixStats('sessionType');

    const recentExecutions = executions
        .slice()
        .sort(
            (a, b) => Date.parse(b.startTime ?? '') - Date.parse(a.startTime ?? ''),
        )
        .slice(0, 8)
        .map(e => {
            const grading = gradingByExecutionId.get(e.id);
            const actorNames = e.actorIds
                .map(id => actorMap.get(id)?.displayName)
                .filter((n): n is string => Boolean(n));
            const primaryActor = e.actorIds.map(id => actorMap.get(id)).find(Boolean) ?? null;

            return {
                ...e,
                statusLabel: getExecutionStatusLabel(e),
                actorLabel: actorNames.length ? actorNames.join(', ') : 'No actor mapped',
                grading,
                actorContext: primaryActor
                    ? ({
                        displayName: primaryActor.displayName ?? null,
                        roleName: primaryActor.roles[0] ?? null,
                        email: primaryActor.email ?? null,
                    } satisfies ExecutionActorContext)
                    : null,
            };
        });

    const ongoingExecutions = executions
        .filter(e => getExecutionLifecycleState(e) === 'ongoing')
        .sort((a, b) => Date.parse(b.startTime ?? '') - Date.parse(a.startTime ?? ''))
        .map(e => {
            const actorNames = e.actorIds
                .map(id => actorMap.get(id)?.displayName)
                .filter((n): n is string => Boolean(n));
            const startTime = Date.parse(e.startTime ?? '');
            const primaryActor = e.actorIds.map(id => actorMap.get(id)).find(Boolean) ?? null;

            return {
                id: e.id,
                actorLabel: actorNames.length ? actorNames.join(', ') : 'No actor mapped',
                executionMode: e.executionMode,
                platform: e.platform,
                startTime: e.startTime,
                elapsedSeconds: Number.isFinite(startTime)
                    ? Math.max(0, (loadedAt.getTime() - startTime) / 1000)
                    : null,
                actorContext: primaryActor
                    ? ({
                        displayName: primaryActor.displayName ?? null,
                        roleName: primaryActor.roles[0] ?? null,
                        email: primaryActor.email ?? null,
                    } satisfies ExecutionActorContext)
                    : null,
            };
        });

    return {
        loadedAt,
        totalExecutions: executions.length,
        completionRate,
        avgDuration,
        uniqueActorCount: uniqueActorIds.size,
        gradedCount,
        reviewedCount,
        gradingCoverage,
        statusCounters,
        topActors,
        modeStats,
        platformStats,
        sessionTypeStats,
        recentExecutions,
        ongoingExecutions,
        procedureDetail,
        issues,
    };
}

// --------------- Loading skeleton --------------------------------------------

const LoadingSkeleton: React.FC<{ styles: ReturnType<typeof useStyles> }> = ({ styles }) => (
    <div className={styles.wrapper}>
        <div className={styles.topBar}>
            <Skeleton variant="rounded" width={140} height={38} />
            <Skeleton variant="rounded" width={320} height={40} />
        </div>
        <div className={styles.hero}>
            <div className={styles.heroHeader}>
                <Skeleton variant="text" width={120} height={18} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
                <Skeleton variant="text" width="56%" height={48} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
                <Skeleton variant="text" width="30%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.28)' }} />
            </div>
            <div className={styles.heroMetaGrid}>
                {[0, 1, 2, 3].map(i => (
                    <div key={i} className={styles.heroMetaCard}>
                        <Skeleton variant="text" width="40%" height={16} sx={{ bgcolor: 'rgba(255,255,255,0.28)' }} />
                        <Skeleton variant="text" width="70%" height={36} sx={{ bgcolor: 'rgba(255,255,255,0.28)' }} />
                        <Skeleton variant="text" width="60%" height={16} sx={{ bgcolor: 'rgba(255,255,255,0.24)' }} />
                    </div>
                ))}
            </div>
        </div>
        <div className={styles.summaryGrid}>
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={styles.summaryCard}>
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="text" width="55%" height={36} />
                    <Skeleton variant="text" width="80%" height={16} />
                </div>
            ))}
        </div>
        {[0, 1, 2].map(row => (
            <div key={row} className={styles.dashboardGrid}>
                {[0, 1].map(col => (
                    <div key={col} className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div style={{ width: '100%' }}>
                                <Skeleton variant="text" width="40%" height={22} />
                                <Skeleton variant="text" width="70%" height={16} />
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            {Array.from({ length: 4 }).map((__, k) => (
                                <Skeleton
                                    key={k}
                                    variant="rounded"
                                    width="100%"
                                    height={44}
                                    style={{ marginBottom: 12 }}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        ))}
    </div>
);

// --------------- Main component ----------------------------------------------

export const ProcedureSummaryPage: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();

    const executionsGridUIStore = useExecutionsGridUIStore();
    const userPermissionsUIStore = useUserPermissionsUIStore();
    const { api } = getEnv<IStoresEnv>(executionsGridUIStore);

    const [range, setRange] = useState<RangeKey>('30d');
    const [reloadKey, setReloadKey] = useState(0);
    const [lastPayload, setLastPayload] = useState<SummaryPayload | null>(null);
    const [chipModal, setChipModal] = useState<ChipModalState>(null);
    const [pulseBreakdown, setPulseBreakdown] = useState(true);
    const [pulseFilters, setPulseFilters] = useState<Set<string>>(() => new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']));
    const [popupFilters, setPopupFilters] = useState<Set<string>>(() => new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']));
    const [pulseBarModal, setPulseBarModal] = useState<{ point: ActivityPoint; anchor: { top: number; left: number; width: number; height: number } } | null>(null);
    useEffect(() => {
        setPopupFilters(new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']));
    }, [pulseBarModal?.point.key]);
    const [procedureImage, setProcedureImage] = useState<string | null>(null);
    const [state, setState] = useState<SummaryState>({
        isLoading: true,
        error: null,
        payload: null,
        issuesLoading: false,
    });

    const goBack = () => {
        if ((location.state as any)?.fromNeoOverview) {
            navigate(`/${RoutePaths.analytics}`);
        } else if (id) {
            navigate(`/${RoutePaths.procedures}/analytics/${id}`);
        } else {
            navigate(`/${RoutePaths.procedures}`);
        }
    };

    const openExecution = (entityId: string, executionId: string) => {
        navigate(`/${RoutePaths.procedures}/analytics/${entityId}/executions/${executionId}`, {
            state: { fromAnalytics: true, fromProcedureInsights: true, insightsId: id },
        });
    };

    // Load procedure image independently (keyed only on id, not range/reload)
    // Uses the Search endpoint because the single-procedure GET does not return procedurePreview.
    useEffect(() => {
        if (!id) {return;}
        let cancelled = false;
        const loadImage = async () => {
            const result = await performGetProcedures(api, {
                payload: {
                    limit: 100, page: 0, searchText: '',
                    sortDirection: 0, sortFieldName: 'name',
                    inGroup: 0, inGroupId: '',
                },
                errorHandlers: { DEFAULT: '' },
            });
            if (!cancelled && result?.success) {
                const entities: Array<Record<string, any>> = Array.isArray(result.data?.entities) ? result.data.entities : [];
                const found = entities.find(p => p.id === id);
                const src = found?.procedurePreview?.src ?? null;
                if (src) {setProcedureImage(src);}
            }
        };
        void loadImage();

        return () => { cancelled = true; };
    }, [api, id]);

    useEffect(() => {
        if (!id) {return;}

        let cancelled = false;

        const load = async () => {
            setState({ isLoading: true, error: null, payload: null, issuesLoading: false });

            const from = getRangeStart(range);
            const to = new Date();
            const formattedFrom = from ? formatLocalDateTime(from) : undefined;
            const formattedTo = formatLocalDateTime(to);
            const dateRange = from ? { From: formattedFrom, To: formattedTo } : {};

            // Phase 1 · executions and entity details in parallel
            const [executionsResult, entityResult] = await Promise.all([
                performGetExecutions(api, {
                    payload: { EntityIds: [id], ...dateRange },
                    errorHandlers: { DEFAULT: '' },
                }),
                performGetEntity(api, {
                    payload: { id },
                    errorHandlers: { DEFAULT: '' },
                }),
            ]);

            if (cancelled) {return;}

            if (!executionsResult.success) {
                setState({
                    isLoading: false,
                    error: 'Unable to load execution data for this procedure.',
                    payload: null,
                    issuesLoading: false,
                });

                return;
            }

            const executions = asArray<Record<string, any>>(executionsResult.data)
                .map(normalizeExecution)
                .filter(e => e.id);

            const entityRaw = entityResult.success ? entityResult.data : null;
            const procedureDetail: ProcedureDetail = {
                id,
                name:
                    getString(entityRaw?.name) ??
                    getString(entityRaw?.entityName) ??
                    executions[0]?.entityName ??
                    null,
                version:
                    getString(entityRaw?.version) ?? getString(entityRaw?.entityVersion) ?? null,
                description:
                    getString(entityRaw?.description) ?? getString(entityRaw?.entityDescription) ?? null,
            };

            const uniqueActorIds = Array.from(
                new Set(executions.flatMap(e => e.actorIds)),
            );
            const executionIds = executions.map(e => e.id);

            // Phase 2 — actors and gradings in parallel
            const [actorsResult, gradingsResult, actorSummaryResult] = await Promise.all([
                performQueryActors(api, {
                    payload: uniqueActorIds.length ? { ActorIds: uniqueActorIds } : {},
                    errorHandlers: { DEFAULT: '' },
                }),
                performGetGradings(api, {
                    payload: { executionIds, ...dateRange },
                    errorHandlers: { DEFAULT: '' },
                }),
                performGetActorsSummary(api, {
                    payload: uniqueActorIds.length ? { ActorIds: uniqueActorIds } : {},
                    errorHandlers: { DEFAULT: '' },
                }),
            ]);

            if (cancelled) {return;}

            const actors = actorsResult.success
                ? asArray<Record<string, any>>(actorsResult.data)
                    .map(normalizeActor)
                    .filter(a => a.id)
                : [];
            const gradings = gradingsResult.success
                ? asArray<Record<string, any>>(gradingsResult.data)
                    .map(normalizeGrading)
                    .filter(
                        g =>
                            g.executionId &&
                            (!executionIds.length || executionIds.includes(g.executionId)),
                    )
                : [];
            const actorSummaries = actorSummaryResult.success
                ? asArray<Record<string, any>>(actorSummaryResult.data)
                    .map(normalizeActorSummary)
                    .filter(s => s.actorId)
                : [];

            const loadedAt = new Date();
            const basePayload: SummaryPayload = {
                executions,
                actors,
                gradings,
                actorSummaries,
                procedureDetail,
                issues: EMPTY_ISSUES,
                loadedAt,
            };

            setState({
                isLoading: false,
                error: null,
                payload: basePayload,
                issuesLoading: true,
            });
            setLastPayload(basePayload);

            // Phase 3 — full-data for issues (separate)
            const fullDataResult = await performGetExecutionsFullData(api, {
                payload: { EntityIds: [id], ...dateRange },
                errorHandlers: { DEFAULT: '' },
            });

            if (cancelled) {return;}

            const issues = fullDataResult.success
                ? buildIssues(asArray<Record<string, any>>(fullDataResult.data))
                : EMPTY_ISSUES;
            const nextPayload: SummaryPayload = { ...basePayload, issues };

            setState({ isLoading: false, error: null, payload: nextPayload, issuesLoading: false });
            setLastPayload(nextPayload);
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [api, id, range, reloadKey]);

    const model = useMemo(
        () => buildModel(state.payload ?? lastPayload),
        [lastPayload, state.payload],
    );

    const pulseSeries = useMemo<ActivityPoint[]>(() => {
        const executions = (state.payload ?? lastPayload)?.executions ?? [];
        if (!executions.length) {return [];}

        const windowDays = getRangeDays(range);
        const bucketSizeDays = windowDays > 31 ? 7 : 1;
        const bucketCount = Math.ceil(windowDays / bucketSizeDays);
        const now = new Date();
        const start = startOfDay(
            new Date(now.getTime() - (windowDays - 1) * 24 * 60 * 60 * 1000),
        );
        const buckets = Array.from({ length: bucketCount }).map((_, i) => {
            const bucketStart = new Date(
                start.getTime() + i * bucketSizeDays * 24 * 60 * 60 * 1000,
            );

            return {
                key: bucketStart.toISOString(),
                label: new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(
                    bucketStart,
                ),
                value: 0,
                completed: 0,
                failed: 0,
                aborted: 0,
                abandoned: 0,
                ongoing: 0,
                executionIds: [] as string[],
                start: bucketStart.getTime(),
                end: new Date(
                    bucketStart.getTime() + bucketSizeDays * 24 * 60 * 60 * 1000,
                ).getTime(),
            };
        });

        executions.forEach(e => {
            const time = Date.parse(e.startTime ?? '');
            if (!Number.isFinite(time) || time < start.getTime()) {return;}
            const bucket = buckets.find(b => time >= b.start && time < b.end);
            if (bucket) {
                bucket.value += 1;
                bucket.executionIds.push(e.id);
                const lifecycle = getExecutionLifecycleState(e);
                if (lifecycle === 'completed') {bucket.completed += 1;} else if (lifecycle === 'failed') {bucket.failed += 1;} else if (lifecycle === 'aborted') {bucket.aborted += 1;} else if (lifecycle === 'abandoned') {bucket.abandoned += 1;} else {bucket.ongoing += 1;}
            }
        });

        return buckets.map(({ key, label, value, completed, failed, aborted, abandoned, ongoing, executionIds }) => ({ key, label, value, completed, failed, aborted, abandoned, ongoing, executionIds }));
    }, [lastPayload, range, state.payload]);

    const filteredPulseSeries = useMemo(() =>
        pulseSeries.map(entry => {
            const completed = pulseFilters.has('completed') ? entry.completed : 0;
            const failed = pulseFilters.has('failed') ? entry.failed : 0;
            const aborted = pulseFilters.has('aborted') ? entry.aborted : 0;
            const abandoned = pulseFilters.has('abandoned') ? entry.abandoned : 0;
            const ongoing = pulseFilters.has('ongoing') ? entry.ongoing : 0;
            const value = completed + failed + aborted + abandoned + ongoing;

            return { ...entry, value, completed, failed, aborted, abandoned, ongoing };
        }),
    [pulseSeries, pulseFilters],
    );

    const maxFilteredPulseValue = Math.max(...filteredPulseSeries.map(p => p.value), 1);

    const pulseTitle =
        range === 'all'
            ? 'Activity pulse: last 30 days'
            : `Activity pulse: ${RANGE_OPTIONS.find(o => o.key === range)?.label ?? range}`;

    if (state.isLoading && !lastPayload) {
        return <LoadingSkeleton styles={styles} />;
    }

    if (state.error) {
        return <div className={styles.errorCard}>{state.error}</div>;
    }

    if (!model || model.totalExecutions === 0) {
        const procedureName =
            model?.procedureDetail?.name ?? lastPayload?.procedureDetail?.name ?? 'this procedure';

        return (
            <div className={styles.wrapper}>
                <div className={styles.topBar}>
                    <div className={styles.topBarLeft}>
                        <button className={styles.backButton} onClick={goBack} type="button">
                            <ArrowBackRounded fontSize="small" />
                            Sessions
                        </button>
                        <span className={styles.pageTitle}>Insights</span>
                    </div>
                </div>
                <div className={styles.controlsRow}>
                    <div className={styles.rangeControls}>
                        {RANGE_OPTIONS.map(opt => (
                            <button
                                key={opt.key}
                                type="button"
                                className={styles.rangeButton}
                                data-active={range === opt.key}
                                onClick={() => setRange(opt.key)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.emptyState}>
                    <div className={styles.emptyTitle}>No executions found</div>
                    <div className={styles.emptyDescription}>
                        {`There are no recorded sessions for ${procedureName} in this time window. Try a wider range.`}
                    </div>
                </div>
            </div>
        );
    }

    const procedureName = model.procedureDetail?.name ?? 'Unknown procedure';
    const procedureVersion = model.procedureDetail?.version;
    const procedureDescription = model.procedureDetail?.description;

    const summaryCards = [
        {
            label: 'Sessions',
            value: formatCompactNumber(model.totalExecutions),
            hint: `${formatWholePercent(model.completionRate)} completed`,
        },
        {
            label: 'Actors',
            value: formatCompactNumber(model.uniqueActorCount),
            hint: 'Unique people who ran this procedure',
        },
        {
            label: 'Avg duration',
            value: formatDuration(model.avgDuration),
            hint: 'Mean session duration in selected window',
        },
        {
            label: 'Completion rate',
            value: formatWholePercent(model.completionRate),
            hint: `${model.statusCounters.completed} of ${model.totalExecutions} sessions completed`,
        },
        {
            label: 'Graded',
            value: formatCompactNumber(model.gradedCount),
            hint: `${formatWholePercent(model.gradingCoverage)} of sessions have grading`,
        },
        {
            label: 'Reviewed',
            value: formatCompactNumber(model.reviewedCount),
            hint: 'Sessions with at least one review',
        },
    ];

    const healthRows = [
        { label: 'Completed', value: model.statusCounters.completed, color: theme.colors.green },
        { label: 'Failed', value: model.statusCounters.failed, color: theme.colors.red },
        {
            label: 'Stopped',
            value: model.statusCounters.aborted,
            color: theme.isDark ? '#e8a060' : '#7a5b00',
        },
        { label: 'Abandoned', value: model.statusCounters.abandoned, color: '#667780' },
        { label: 'In progress', value: model.statusCounters.ongoing, color: '#d18100' },
    ];

    const visibleTopActors = model.topActors.map(actor => ({
        ...actor,
        visibleRoles: userPermissionsUIStore.canEditUser(actor.roles) ? actor.roles.slice(0, 2) : [],
        initials: actor.displayName
            .split(' ')
            .slice(0, 2)
            .map(part => part[0]?.toUpperCase() ?? '')
            .join(''),
    }));

    const failureRatio =
        ((model.statusCounters.failed + model.statusCounters.aborted + model.statusCounters.abandoned) /
            Math.max(model.totalExecutions, 1)) *
        100;

    return (
        <>
            <div className={styles.wrapper}>
                {/* ── Top bar ── */}
                <div className={styles.topBar}>
                    <div className={styles.topBarLeft}>
                        <button className={styles.backButton} onClick={goBack} type="button">
                            <ArrowBackRounded fontSize="small" />
                        Sessions
                        </button>
                        <span className={styles.pageTitle}>
                            <AutoGraphRounded fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Insights
                        </span>
                    </div>
                    <div className={styles.topBarActions}>
                        <div className={styles.refreshMeta}>
                            <span className={styles.refreshMetaLabel}>
                                {`Updated ${formatSubtleDateLabel(model.loadedAt.toISOString())}`}
                            </span>
                            <Button
                                className={styles.refreshButton}
                                onClick={() => setReloadKey(k => k + 1)}
                                loading={state.isLoading}
                                color="primary"
                            >
                            Refresh
                            </Button>
                        </div>
                    </div>
                </div>

                {/* ── Hero ── */}
                <div className={styles.hero}>
                    <div className={styles.heroGlow} />
                    <div className={styles.heroGlowSecondary} />
                    <div className={styles.heroHeaderRow}>
                        <div className={styles.heroImageWrap}>
                            {procedureImage
                                ? <img src={procedureImage} alt="" className={styles.heroImg} />
                                : <img src={ProceduresIcon} className={styles.heroImgPlaceholder} alt="" />
                            }
                        </div>
                        <div className={styles.heroHeader}>
                            <div className={styles.heroEyebrow}>Procedure Insights</div>
                            <div className={styles.heroTitle}>{procedureName}</div>
                            <div className={styles.heroBadgeRow}>
                                {procedureVersion && (
                                    <span className={styles.heroBadge}>{`v${procedureVersion}`}</span>
                                )}
                                {procedureDescription && (
                                    <span className={styles.heroBadge}>{procedureDescription}</span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.heroAllSessionsBtn}
                            onClick={() => id && navigate(`/${RoutePaths.procedures}/analytics/${id}`)}
                        >
                        All Sessions
                            <ArrowForwardRounded style={{ width: 15, height: 15 }} />
                        </button>
                    </div>
                    <div className={styles.heroMetaGrid}>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Total sessions</div>
                            <div className={styles.heroMetaValue}>{formatCompactNumber(model.totalExecutions)}</div>
                            <div className={styles.heroMetaHint}>
                                {`${RANGE_OPTIONS.find(o => o.key === range)?.label ?? range}`}
                            </div>
                        </div>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Completion rate</div>
                            <div className={styles.heroMetaValue}>
                                {formatWholePercent(model.completionRate)}
                            </div>
                            <div className={styles.heroMetaHint}>
                                {`${model.statusCounters.completed} completed of ${model.totalExecutions} total`}
                            </div>
                        </div>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Failure watch</div>
                            <div className={styles.heroMetaValue}>{formatWholePercent(failureRatio)}</div>
                            <div className={styles.heroMetaHint}>
                                {`${model.statusCounters.failed} failed, ${model.statusCounters.aborted} stopped, ${model.statusCounters.abandoned} abandoned`}
                            </div>
                        </div>
                        <div className={styles.heroMetaCard}>
                            <div className={styles.heroMetaLabel}>Top platform</div>
                            <div className={styles.heroMetaValue}>
                                {model.platformStats[0]?.label ?? '—'}
                            </div>
                            <div className={styles.heroMetaHint}>
                                {model.platformStats[0]
                                    ? `${formatWholePercent(model.platformStats[0].pct)} of sessions`
                                    : 'No platform data'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Range selector ── */}
                <div className={styles.controlsRow}>
                    <div className={styles.rangeControls}>
                        {RANGE_OPTIONS.map(opt => (
                            <button
                                key={opt.key}
                                type="button"
                                className={styles.rangeButton}
                                data-active={range === opt.key}
                                onClick={() => setRange(opt.key)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Summary stat cards ── */}
                <div className={styles.summaryGrid}>
                    {summaryCards.map(card => (
                        <div key={card.label} className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>{card.label}</div>
                            <div className={styles.summaryValue}>{card.value}</div>
                            <div className={styles.summaryHint}>{card.hint}</div>
                        </div>
                    ))}
                </div>

                {/* ── Ongoing sessions ── */}
                {model.ongoingExecutions.length > 0 && (
                    <div className={styles.panelFull}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Sessions in progress</div>
                                <div className={styles.panelSubtitle}>
                                Live sessions running right now for this procedure.
                                </div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.ongoingStrip}>
                                {model.ongoingExecutions.map(e => (
                                    <button
                                        key={e.id}
                                        type="button"
                                        className={styles.ongoingCard}
                                        onClick={() => id && openExecution(id, e.id)}
                                    >
                                        <div className={styles.ongoingCardHeader}>
                                            <span className={styles.ongoingTitle}>{procedureName}</span>
                                        </div>
                                        <div className={styles.ongoingMeta}>
                                            <span>{e.actorLabel}</span>
                                            <span>{e.executionMode}</span>
                                            <span>{e.platform}</span>
                                        </div>
                                        <div className={styles.ongoingMeta}>
                                            <span>{`Started ${formatDateLabel(e.startTime)}`}</span>
                                            <span>{`Elapsed ${formatDuration(e.elapsedSeconds)}`}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Activity pulse + Session health ── */}
                <div className={styles.dashboardGrid}>
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>{pulseTitle}</div>
                                <div className={styles.panelSubtitle}>
                                Volume over time · spot bursts, plateaus, and quiet periods.
                                </div>
                            </div>
                            <button
                                type="button"
                                className={styles.pulseToggle}
                                data-active={pulseBreakdown}
                                onClick={() => setPulseBreakdown(v => !v)}
                            >
                            Breakdown
                            </button>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.activityBars}>
                                {filteredPulseSeries.map(entry => (
                                    <div
                                        key={entry.key}
                                        className={styles.activityBarColumn}
                                        title={`${entry.label}: ${entry.value} sessions`}
                                        style={{
                                            ...(entry.value > 0 ? { cursor: 'pointer' } : undefined),
                                            ...(pulseBarModal?.point.key === entry.key ? {
                                                filter: 'drop-shadow(0 0 2px rgba(108, 52, 239, 0.7))',
                                                borderRadius: 4,
                                            } : undefined),
                                        }}
                                        onClick={e => {
                                            if (entry.value === 0) {return;}
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setChipModal(null);
                                            setPulseBarModal({
                                                point: entry,
                                                anchor: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
                                            });
                                        }}
                                    >
                                        {entry.value > 0 && (
                                            <div className={styles.activityBarCount}>{entry.value}</div>
                                        )}
                                        {pulseBreakdown && entry.value > 0 ? (
                                            <div
                                                className={styles.activityBarStacked}
                                                style={{ height: `${Math.max(12, (entry.value / maxFilteredPulseValue) * 140)}px` }}
                                            >
                                                {([
                                                    { key: 'completed', color: theme.colors.green, count: entry.completed },
                                                    { key: 'ongoing', color: theme.colors.blue_primary, count: entry.ongoing },
                                                    { key: 'abandoned', color: theme.colors.grey_text, count: entry.abandoned },
                                                    { key: 'aborted', color: '#d18100', count: entry.aborted },
                                                    { key: 'failed', color: theme.colors.red, count: entry.failed },
                                                ] as const).map(({ key: sKey, color, count }) => count > 0 && (
                                                    <div
                                                        key={sKey}
                                                        className={styles.activityBarSegment}
                                                        style={{
                                                            height: `${(count / entry.value) * 100}%`,
                                                            background: color,
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <div
                                                className={styles.activityBarValue}
                                                style={{
                                                    height: `${Math.max(12, (entry.value / maxFilteredPulseValue) * 140)}px`,
                                                    opacity: entry.value === 0 ? 0.2 : 1,
                                                }}
                                            />
                                        )}
                                        <div className={styles.activityBarLabel}>{entry.label}</div>
                                    </div>
                                ))}
                            </div>
                            {pulseBreakdown && (
                                <div className={styles.pulseLegend}>
                                    {([
                                        { key: 'completed', label: 'Completed', color: theme.colors.green },
                                        { key: 'failed', label: 'Failed', color: theme.colors.red },
                                        { key: 'aborted', label: 'Stopped', color: '#d18100' },
                                        { key: 'abandoned', label: 'Abandoned', color: theme.colors.grey_text },
                                        { key: 'ongoing', label: 'In progress', color: theme.colors.blue_primary },
                                    ] as const).map(({ key, label: lbl, color }) => (
                                        <button
                                            key={lbl}
                                            type="button"
                                            className={styles.pulseLegendItem}
                                            style={{ opacity: pulseFilters.has(key) ? 1 : 0.3 }}
                                            onClick={() => setPulseFilters(prev => {
                                                const next = new Set(prev);
                                                if (next.has(key)) {next.delete(key);} else {next.add(key);}

                                                return next;
                                            })}
                                        >
                                            <div className={styles.pulseLegendDot} style={{ background: color }} />
                                            {lbl}
                                        </button>
                                    ))}
                                    <div className={styles.pulseLegendCtrl}>
                                        <button
                                            type="button"
                                            className={styles.pulseLegendCtrlBtn}
                                            onClick={() => setPulseFilters(new Set(['completed', 'failed', 'aborted', 'abandoned', 'ongoing']))}
                                        >All</button>
                                        <button
                                            type="button"
                                            className={styles.pulseLegendCtrlBtn}
                                            onClick={() => setPulseFilters(new Set())}
                                        >None</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Session health</div>
                                <div className={styles.panelSubtitle}>
                                Outcome breakdown · failures, stops, and abandonment vs completions.
                                </div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.healthList}>
                                {healthRows.map(row => (
                                    <div key={row.label} className={styles.healthRow}>
                                        <div className={styles.healthLabel}>{row.label}</div>
                                        <div className={styles.track}>
                                            <div
                                                className={styles.fill}
                                                style={{
                                                    width: `${
                                                        model.totalExecutions
                                                            ? (row.value / model.totalExecutions) * 100
                                                            : 0
                                                    }%`,
                                                    background: row.color,
                                                }}
                                            />
                                        </div>
                                        <div className={styles.healthValue}>{row.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Top actors + Platform/mode mix + Recent sessions ── */}
                <div className={styles.dashboardGrid}>
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Top actors</div>
                                <div className={styles.panelSubtitle}>
                                Most active practitioners · jump into their session history.
                                </div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            <div className={styles.list}>
                                {visibleTopActors.map(actor => (
                                    <div
                                        key={actor.actorId}
                                        className={styles.listRowInteractive}
                                        onClick={() =>
                                            navigate(`/${RoutePaths.users}/analytics/${actor.actorId}`)
                                        }
                                    >
                                        <div className={styles.actorRow}>
                                            <div className={styles.actorAvatar}>{actor.initials}</div>
                                            <div className={styles.listMain}>
                                                <button
                                                    type="button"
                                                    className={styles.linkButton}
                                                    onClick={() =>
                                                        navigate(
                                                            `/${RoutePaths.users}/analytics/${actor.actorId}`,
                                                        )
                                                    }
                                                >
                                                    <span className={styles.listTitle}>
                                                        {actor.displayName}
                                                    </span>
                                                </button>
                                                <div className={styles.listMeta}>
                                                    {actor.email && <span>{actor.email}</span>}
                                                    {actor.visibleRoles.map(role => (
                                                        <span key={role} className={styles.roleBadge}>
                                                            {role}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.listCount}>{actor.totalExecutions}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <div className={styles.panelTitle}>Execution context</div>
                                <div className={styles.panelSubtitle}>
                                Session outcomes by mode and platform · each bar shows how many sessions ended in that state.
                                </div>
                            </div>
                        </div>
                        <div className={styles.panelBody}>
                            {[
                                { title: 'Mode', stats: model.modeStats, keyPrefix: 'mode' },
                                { title: 'Platform', stats: model.platformStats, keyPrefix: 'platform' },
                                { title: 'Participants', stats: model.sessionTypeStats, keyPrefix: 'sessionType' },
                            ].map(({ title, stats, keyPrefix }) => (
                                <div key={keyPrefix} className={styles.mixSection}>
                                    <div className={styles.mixSectionTitle}>{title}</div>
                                    {stats.length === 0 ? (
                                        <span className={styles.mixEmptyState}>No data</span>
                                    ) : (
                                        <div className={styles.mixCardGrid}>
                                            {stats.map(stat => (
                                                <div key={`${keyPrefix}-${stat.label}`} className={styles.mixCard}>
                                                    <div className={styles.mixCardHeader}>
                                                        <span className={styles.mixCardLabel} title={stat.label}>{stat.label}</span>
                                                        <div className={styles.mixCardMetaGroup}>
                                                            {([
                                                                { key: 'sessions' as const, label: `${stat.count} session${stat.count !== 1 ? 's' : ''}`, title: 'Sessions over time' },
                                                                { key: 'actors' as const, label: `${stat.uniqueActors} actor${stat.uniqueActors !== 1 ? 's' : ''}`, title: 'Actors' },
                                                                ...(stat.avgDuration !== null ? [{ key: 'avg' as const, label: `avg ${formatDuration(stat.avgDuration)}`, title: 'Duration distribution' }] : []),
                                                            ]).map(({ key: chipType, label: chipLabel, title: chipTitle }) => {
                                                                const isActive = chipModal?.stat === stat && chipModal?.chipType === chipType;

                                                                return (
                                                                    <button
                                                                        key={chipType}
                                                                        type="button"
                                                                        className={`${styles.mixCardMetaChip} ${isActive ? styles.mixCardMetaChipActive : ''}`}
                                                                        onClick={e => {
                                                                            if (isActive) {
                                                                                setChipModal(null);
                                                                            } else {
                                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                                setChipModal({ stat, chipType, title: chipTitle, anchor: { top: rect.top, left: rect.left, width: rect.width, height: rect.height } });
                                                                            }
                                                                        }}
                                                                    >
                                                                        {chipLabel}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className={styles.mixCardStatusRows}>
                                                        {(['completed', 'failed', 'aborted', 'abandoned', 'ongoing'] as const)
                                                            .filter(s => stat.statusBreakdown[s] > 0)
                                                            .map(s => {
                                                                const cnt = stat.statusBreakdown[s];
                                                                const pct = stat.count ? (cnt / stat.count) * 100 : 0;
                                                                const color =
                                                                s === 'completed' ? theme.colors.green
                                                                    : s === 'failed' ? theme.colors.red
                                                                        : s === 'aborted' ? '#d18100'
                                                                            : s === 'abandoned' ? theme.colors.grey_text
                                                                                : theme.colors.blue_primary;
                                                                const statusLabel =
                                                                s === 'completed' ? 'Completed'
                                                                    : s === 'failed' ? 'Failed'
                                                                        : s === 'aborted' ? 'Stopped'
                                                                            : s === 'abandoned' ? 'Abandoned'
                                                                                : 'In progress';

                                                                return (
                                                                    <div key={s} className={styles.mixStatusRow}>
                                                                        <span className={styles.mixStatusDot} style={{ background: color }} />
                                                                        <span className={styles.mixStatusLabel}>{statusLabel}</span>
                                                                        <div className={styles.mixStatusBarTrack}>
                                                                            <div
                                                                                className={styles.mixStatusBarFill}
                                                                                style={{ width: `${Math.max(pct, cnt > 0 ? 2 : 0)}%`, background: color }}
                                                                            />
                                                                        </div>
                                                                        <span className={styles.mixStatusCount}>{cnt}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Recent executions ── */}
                <div className={styles.panelFull}>
                    <div className={styles.panelHeader}>
                        <div>
                            <div className={styles.panelTitle}>Recent sessions</div>
                            <div className={styles.panelSubtitle}>
                            Latest executions — click any card to drill into the full session.
                            </div>
                        </div>
                    </div>
                    <div className={styles.panelBody}>
                        <div className={styles.recentGrid}>
                            {model.recentExecutions.map(execution => {
                                const tone = getStatusTone(execution.statusLabel, theme);

                                return (
                                    <div
                                        key={execution.id}
                                        className={styles.recentCardInteractive}
                                        onClick={() => openExecution(execution.entityId, execution.id)}
                                    >
                                        <span className={styles.recentTitle}>{execution.actorLabel}</span>
                                        <div className={styles.recentMetaChips}>
                                            <span className={styles.recentMetaChip}>
                                                {formatDateLabel(execution.startTime)}
                                            </span>
                                            {execution.entityVersion && (
                                                <span className={styles.recentMetaChip}>
                                                    {`v${execution.entityVersion}`}
                                                </span>
                                            )}
                                            <span className={styles.recentMetaChip}>
                                                {execution.executionMode}
                                            </span>
                                            <span className={styles.recentMetaChip}>
                                                {execution.platform}
                                            </span>
                                            {formatDuration(execution.durationSeconds) !== 'n/a' && (
                                                <span className={styles.recentMetaChip}>
                                                    {formatDuration(execution.durationSeconds)}
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.chipRow}>
                                            <span
                                                className={styles.statusPill}
                                                style={{
                                                    color: tone.color,
                                                    background: tone.background,
                                                }}
                                            >
                                                {execution.statusLabel}
                                            </span>
                                            {execution.grading && (
                                                <span
                                                    className={styles.statusPill}
                                                    style={{
                                                        color: theme.colors.violet_primary,
                                                        background: theme.colors.transp_violet,
                                                    }}
                                                >
                                                    {execution.grading.isReviewed
                                                        ? `Reviewed ${execution.grading.reviewCount}×`
                                                        : 'Graded'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ── Operational pressure points ── */}
                <div className={styles.panelFull}>
                    <div className={styles.panelHeader}>
                        <div>
                            <div className={styles.panelTitle}>Operational pressure points</div>
                            <div className={styles.panelSubtitle}>
                                {state.issuesLoading
                                    ? 'Issue metrics are loading · the rest of the dashboard is already available.'
                                    : 'Step-level timing, failure rates, and abandonment patterns for this procedure.'}
                            </div>
                        </div>
                    </div>
                    <div className={styles.panelBody}>
                        {state.issuesLoading ? (
                            <div>
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        variant="rounded"
                                        width="100%"
                                        height={42}
                                        style={{ marginBottom: 10 }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className={styles.issueGrid}>
                                <div className={styles.issueColumn}>
                                    <div className={styles.issueColumnHeader}>
                                        <div className={styles.issueTitle}>Longest steps</div>
                                    </div>
                                    <div className={styles.issueList}>
                                        {model.issues.longestSteps.length === 0 ? (
                                            <span className={styles.issueValue}>No timing data</span>
                                        ) : (
                                            model.issues.longestSteps.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.executionId ? (
                                                        <button
                                                            type="button"
                                                            className={styles.linkButton}
                                                            onClick={() =>
                                                                id &&
                                                            item.executionId &&
                                                            openExecution(id, item.executionId)
                                                            }
                                                        >
                                                            <span className={styles.issueName}>
                                                                {item.name}
                                                            </span>
                                                        </button>
                                                    ) : (
                                                        <span className={styles.issueName}>{item.name}</span>
                                                    )}
                                                    <span className={styles.issueValue}>
                                                        {formatDuration(item.averageDuration)}
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div className={styles.issueColumn}>
                                    <div className={styles.issueColumnHeader}>
                                        <div className={styles.issueTitle}>Most failed steps</div>
                                    </div>
                                    <div className={styles.issueList}>
                                        {model.issues.failedSteps.length === 0 ? (
                                            <span className={styles.issueValue}>No failed step data</span>
                                        ) : (
                                            model.issues.failedSteps.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.executionId ? (
                                                        <button
                                                            type="button"
                                                            className={styles.linkButton}
                                                            onClick={() =>
                                                                id &&
                                                            item.executionId &&
                                                            openExecution(id, item.executionId)
                                                            }
                                                        >
                                                            <span className={styles.issueName}>
                                                                {item.name}
                                                            </span>
                                                        </button>
                                                    ) : (
                                                        <span className={styles.issueName}>{item.name}</span>
                                                    )}
                                                    <span className={styles.issueValue}>
                                                        {`${item.count} · ${formatWholePercent(
                                                            (item.failureRate ?? 0) * 100,
                                                        )}`}
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div className={styles.issueColumn}>
                                    <div className={styles.issueColumnHeader}>
                                        <div className={styles.issueTitle}>Abandoned at step</div>
                                    </div>
                                    <div className={styles.issueList}>
                                        {model.issues.abandonedSteps.length === 0 ? (
                                            <span className={styles.issueValue}>No abandonment data</span>
                                        ) : (
                                            model.issues.abandonedSteps.map(item => (
                                                <div key={item.name} className={styles.issueRow}>
                                                    {item.executionId ? (
                                                        <button
                                                            type="button"
                                                            className={styles.linkButton}
                                                            onClick={() =>
                                                                id &&
                                                            item.executionId &&
                                                            openExecution(id, item.executionId)
                                                            }
                                                        >
                                                            <span className={styles.issueName}>
                                                                {item.name}
                                                            </span>
                                                        </button>
                                                    ) : (
                                                        <span className={styles.issueName}>{item.name}</span>
                                                    )}
                                                    <span className={styles.issueValue}>
                                                        {`${item.count}×`}
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Chip detail popover ── */}
            {chipModal && (() => {
                const { stat, chipType, title, anchor } = chipModal;
                // Position below the chip, clamped to viewport
                const POPOVER_WIDTH = 360;
                const POPOVER_OFFSET = 6;
                const top = anchor.top + anchor.height + POPOVER_OFFSET;
                const leftRaw = anchor.left + anchor.width / 2 - POPOVER_WIDTH / 2;
                const left = Math.max(12, Math.min(leftRaw, window.innerWidth - POPOVER_WIDTH - 12));
                // Arrow offset relative to popover left edge
                const arrowLeft = Math.max(10, Math.min(anchor.left + anchor.width / 2 - left, POPOVER_WIDTH - 10));

                return ReactDOM.createPortal(
                    <>
                        {/* transparent backdrop for click-outside */}
                        <div className={styles.chipPopoverBackdrop} onClick={() => setChipModal(null)} />
                        <div
                            className={styles.chipPopoverBox}
                            style={{ top, left, width: POPOVER_WIDTH }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* arrow */}
                            <div className={styles.chipPopoverArrow} style={{ left: arrowLeft }} />
                            <div className={styles.pulseBarPopupAccent} />
                            <div className={styles.pulseBarPopupHeaderContent}>
                                <div className={styles.pulseBarPopupHeaderTop}>
                                    <div>
                                        <div className={styles.pulseBarPopupDate}>{stat.label}</div>
                                        <div className={styles.pulseBarPopupCount}>{title}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.chipModalClose}
                                        onClick={() => setChipModal(null)}
                                    >
                                    ✕
                                    </button>
                                </div>
                            </div>
                            <div className={styles.chipModalBody}>
                                {chipType === 'sessions' && (() => {
                                    const pts = stat.timeline;
                                    const spanDays = pts.length;
                                    const fmtDate = (d: string) => {
                                        const parsed = new Date(d);

                                        return spanDays > 60
                                            ? parsed.toLocaleDateString('en', { month: 'short', year: '2-digit' })
                                            : parsed.toLocaleDateString('en', { month: 'short', day: 'numeric' });
                                    };
                                type Bucket = { label: string; count: number };
                                let buckets: Bucket[];
                                if (pts.length <= 14) {
                                    buckets = pts.map(p => ({ label: fmtDate(p.date), count: p.count }));
                                } else if (pts.length <= 90) {
                                    const weekMap = new Map<string, number>();
                                    pts.forEach(p => {
                                        const d = new Date(p.date);
                                        const weekStart = new Date(d);
                                        weekStart.setDate(d.getDate() - d.getDay());
                                        const wk = weekStart.toISOString().slice(0, 10);
                                        weekMap.set(wk, (weekMap.get(wk) ?? 0) + p.count);
                                    });
                                    buckets = Array.from(weekMap.entries())
                                        .sort((a, b) => a[0].localeCompare(b[0]))
                                        .map(([wk, count]) => ({ label: fmtDate(wk), count }));
                                } else {
                                    const moMap = new Map<string, number>();
                                    pts.forEach(p => {
                                        const mo = p.date.slice(0, 7);
                                        moMap.set(mo, (moMap.get(mo) ?? 0) + p.count);
                                    });
                                    buckets = Array.from(moMap.entries())
                                        .sort((a, b) => a[0].localeCompare(b[0]))
                                        .map(([mo, count]) => ({ label: new Date(mo + '-01').toLocaleDateString('en', { month: 'short', year: '2-digit' }), count }));
                                }
                                const bMax = Math.max(...buckets.map(b => b.count), 1);

                                return buckets.length === 0 ? (
                                    <span className={styles.chipPanelEmpty}>No timeline data</span>
                                ) : (
                                    <div className={styles.chipModalBarChart}>
                                        {buckets.map((b, i) => (
                                            <div key={i} className={styles.chipModalBarCol}>
                                                <span className={styles.chipModalBarColValue}>{b.count}</span>
                                                <div className={styles.chipModalBarColTrack}>
                                                    <div
                                                        className={styles.chipModalBarColFill}
                                                        style={{ height: `${(b.count / bMax) * 100}%` }}
                                                    />
                                                </div>
                                                <span className={styles.chipModalBarColLabel}>{b.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                                })()}
                                {chipType === 'actors' && (
                                    stat.actorDetails.length === 0 ? (
                                        <span className={styles.chipPanelEmpty}>No actor data</span>
                                    ) : (
                                        <div className={styles.chipModalActorList}>
                                            {stat.actorDetails.map(actor => (
                                                <div key={actor.id} className={styles.chipModalActorRow}>
                                                    <div className={styles.chipModalActorAvatar}>
                                                        {actor.displayName.split(' ').slice(0, 2).map(p => p[0] ?? '').join('').toUpperCase()}
                                                    </div>
                                                    <span className={styles.chipModalActorName}>{actor.displayName}</span>
                                                    <span className={styles.chipModalActorCount}>{`${actor.count} session${actor.count !== 1 ? 's' : ''}`}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                )}
                                {chipType === 'avg' && stat.durations.length > 0 && (() => {
                                    const sorted = stat.durations;
                                    const BIN_COUNT = 10;
                                    const lo = sorted[0];
                                    const hi = sorted[sorted.length - 1];
                                    const binSize = Math.max((hi - lo) / BIN_COUNT, 1);
                                    const bins = Array.from({ length: BIN_COUNT }, (_, i) => ({
                                        label: formatDuration(lo + i * binSize),
                                        count: 0,
                                    }));
                                    sorted.forEach(d => {
                                        const idx = Math.min(Math.floor((d - lo) / binSize), BIN_COUNT - 1);
                                        bins[idx].count += 1;
                                    });
                                    const bMax = Math.max(...bins.map(b => b.count), 1);

                                    return (
                                        <>
                                            <div className={styles.chipModalDurationStats}>
                                                {[
                                                    { key: 'Min', val: stat.minDuration },
                                                    { key: 'Avg', val: stat.avgDuration },
                                                    { key: 'Max', val: stat.maxDuration },
                                                ].map(({ key, val }) => (
                                                    <div key={key} className={styles.chipModalDurationCard}>
                                                        <span className={styles.chipModalDurationLabel}>{key}</span>
                                                        <span className={styles.chipModalDurationValue}>{formatDuration(val)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className={styles.chipModalBarChart}>
                                                {bins.map((b, i) => (
                                                    <div key={i} className={styles.chipModalBarCol}>
                                                        <span className={styles.chipModalBarColValue}>{b.count > 0 ? b.count : ''}</span>
                                                        <div className={styles.chipModalBarColTrack}>
                                                            <div
                                                                className={styles.chipModalBarColFill}
                                                                style={{ height: `${(b.count / bMax) * 100}%` }}
                                                            />
                                                        </div>
                                                        <span className={styles.chipModalBarColLabel}>{b.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </>,
                    document.body,
                );
            })()}

            {/* ── Pulse-bar detail popup ── */}
            {pulseBarModal && (() => {
                const { point, anchor } = pulseBarModal;
                const POPOVER_WIDTH = 380;
                const POPOVER_OFFSET = 8;
                const barCenterY = anchor.top + anchor.height / 2;
                const topRaw = barCenterY - 80;
                const top = Math.max(8, Math.min(topRaw, window.innerHeight - window.innerHeight * 0.55 - 8));
                const rightLeft = anchor.left + anchor.width + POPOVER_OFFSET;
                const hasRightSpace = rightLeft + POPOVER_WIDTH <= window.innerWidth - 8;
                const side: 'right' | 'left' = hasRightSpace ? 'right' : 'left';
                const left = side === 'right' ? rightLeft : anchor.left - POPOVER_WIDTH - POPOVER_OFFSET;
                const arrowTop = barCenterY - top - 7;
                const allExecutions = (state.payload ?? lastPayload)?.executions ?? [];
                const allActors = (state.payload ?? lastPayload)?.actors ?? [];
                const bucketExecutions = allExecutions
                    .filter(e => {
                        if (!point.executionIds.includes(e.id)) {return false;}
                        const lc = getExecutionLifecycleState(e);
                        const k = lc === 'completed' ? 'completed' : lc === 'failed' ? 'failed' : lc === 'aborted' ? 'aborted' : lc === 'abandoned' ? 'abandoned' : 'ongoing';

                        return popupFilters.has(k);
                    })
                    .sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? ''));
                const rawPoint = pulseSeries.find(p => p.key === point.key) ?? point;

                return ReactDOM.createPortal(
                    <>
                        <div className={styles.chipPopoverBackdrop} onClick={() => setPulseBarModal(null)} />
                        <div
                            className={styles.chipPopoverBox}
                            style={{ top, left, width: POPOVER_WIDTH }}
                            onClick={e => e.stopPropagation()}
                        >
                            {side === 'right'
                                ? <div className={styles.pulseBarArrowLeft} style={{ top: arrowTop }} />
                                : <div className={styles.pulseBarArrowRight} style={{ top: arrowTop }} />}
                            <div className={styles.pulseBarPopupAccent} />
                            <div className={styles.pulseBarPopupHeaderContent}>
                                <div className={styles.pulseBarPopupHeaderTop}>
                                    <div>
                                        <div className={styles.pulseBarPopupDate}>{point.label}</div>
                                        <div className={styles.pulseBarPopupCount}>{`${rawPoint.value} session${rawPoint.value !== 1 ? 's' : ''}`}</div>
                                    </div>
                                    <button type="button" className={styles.chipModalClose} onClick={() => setPulseBarModal(null)}>✕</button>
                                </div>
                                <div className={styles.pulseBarPopupSummaryRow}>
                                    {([
                                        { key: 'completed', label: 'Completed', count: rawPoint.completed, color: theme.colors.green },
                                        { key: 'failed', label: 'Failed', count: rawPoint.failed, color: theme.colors.red },
                                        { key: 'aborted', label: 'Stopped', count: rawPoint.aborted, color: '#d18100' },
                                        { key: 'abandoned', label: 'Abandoned', count: rawPoint.abandoned, color: theme.colors.grey_text },
                                        { key: 'ongoing', label: 'In progress', count: rawPoint.ongoing, color: theme.colors.blue_primary },
                                    ] as const).filter(p => p.count > 0).map(({ key: k, label: lbl, count, color }) => (
                                        <button
                                            key={k}
                                            type="button"
                                            className={styles.pulseBarPopupSummaryPill}
                                            style={{ opacity: popupFilters.has(k) ? 1 : 0.4 }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setPopupFilters(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(k)) {next.delete(k);} else {next.add(k);}

                                                    return next;
                                                });
                                            }}
                                        >
                                            <div className={styles.pulseBarPopupSummaryDot} style={{ background: color }} />
                                            {`${count} ${lbl}`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.pulseBarPopupScroller}>
                                <div className={styles.pulseBarPopupList}>
                                    {bucketExecutions.map(execution => {
                                        const status = getExecutionStatusLabel(execution);
                                        const tone = getStatusTone(status, theme);
                                        const resolvedActors = execution.actorIds.map(actorId => allActors.find(a => a.id === actorId));
                                        const primaryActor = resolvedActors[0];
                                        const actorNames = resolvedActors
                                            .map(actor => actor?.displayName || actor?.email || 'Unknown actor')
                                            .join(', ') || 'Unknown actor';
                                        const actorEmail = resolvedActors.length === 1 && primaryActor?.email && primaryActor.email !== primaryActor.displayName ? primaryActor.email : null;
                                        const initials = (primaryActor?.displayName ?? actorNames)
                                            .split(' ')
                                            .filter(Boolean)
                                            .slice(0, 2)
                                            .map(w => w[0]?.toUpperCase() ?? '')
                                            .join('');

                                        return (
                                            <div
                                                key={execution.id}
                                                className={styles.pulseBarPopupRow}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => {
                                                    setPulseBarModal(null);
                                                    openExecution(execution.entityId, execution.id);
                                                }}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        setPulseBarModal(null);
                                                        openExecution(execution.entityId, execution.id);
                                                    }
                                                }}
                                            >
                                                <div className={styles.pulseBarPopupAvatar}>{initials}</div>
                                                <div className={styles.pulseBarPopupRowMain}>
                                                    <div className={styles.pulseBarPopupName}>{actorNames}</div>
                                                    {actorEmail && (
                                                        <div className={styles.pulseBarPopupRowSub}>{actorEmail}</div>
                                                    )}
                                                    {execution.startTime && (
                                                        <div className={styles.pulseBarPopupRowSub}>
                                                            {new Date(execution.startTime).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                                                            {' · '}
                                                            {new Date(execution.startTime).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    )}
                                                    {execution.durationSeconds !== null && (
                                                        <div className={styles.pulseBarPopupRowSub}>{formatDuration(execution.durationSeconds)}</div>
                                                    )}
                                                </div>
                                                <div className={styles.pulseBarPopupBadges}>
                                                    <span
                                                        className={styles.pulseBarPopupBadge}
                                                        style={{ color: theme.colors.grey_text, background: theme.colors.grey_light }}
                                                    >
                                                        {execution.executionMode}
                                                    </span>
                                                    <span
                                                        className={styles.pulseBarPopupBadge}
                                                        style={{ color: tone.color, background: tone.background }}
                                                    >
                                                        {status}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>,
                    document.body,
                );
            })()}
        </>
    );
};
